import { MouseEvent, TouchEvent, useEffect, useRef } from 'react';

import { Header } from '@tanstack/react-table';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { TableStore } from './table-store';
import { DataItemType } from './types';

export const Resizer = <DataType extends DataItemType>({
  header,
  store,
}: {
  header: Header<DataType, any>;
  store: TableStore;
}) => {
  const { useColumnSizing, actions } = store;
  const { userResized, setUserResized } = useColumnSizing();

  const isResizingRef = useRef(false);

  useEffect(() => {
    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        actions.persist();
      }
    };

    const handleTouchEnd = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        actions.persist();
      }
    };

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [actions]);

  try {
    const isResizable = header.column.getCanResize();
    if (!isResizable) {
      return null;
    }
  } catch (e) {
    return null;
  }

  const onResizeStart = (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    isResizingRef.current = true;
    e.stopPropagation();
    e.preventDefault();

    if (!userResized.has(header.id)) {
      setUserResized(new Set([...userResized, header.id]));
    }

    try {
      const resizeHandler = header.getResizeHandler();
      resizeHandler(e);
    } catch (e) {}
  };

  return <StyledResizer onMouseDown={onResizeStart} onTouchStart={onResizeStart} />;
};

export const StyledResizer = styled.div({
  position: 'absolute',
  right: 0,
  height: '100%',
  width: pxToRem(8),
  borderRightWidth: pxToRem(2),
  borderRightColor: 'transparent',
  borderRightStyle: 'solid',
  transition: 'border-right-color 0.25s ease-out',
  '&:hover': {
    borderRightColor: theme.color.elementBlue100,
    cursor: 'col-resize',
  },
});
